import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    Box,
    IconButton,
    Tooltip,
    Chip,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    asyncGetAnswers,
    asyncCreateAnswer,
    asyncUpdateAnswer,
} from '../../../../../../../services/survey.service';
import { ToastSwal } from '../../../../../../components/general/toastSwals/toastSwal';
import FieldText from '../../../../../../components/general/fields/fieldText';
import LoadingButtonGeneral from '../../../../../../components/general/buttons/loadingButtonGeneral';
import { Table } from '../../../../../../components/general/table';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';

interface Props {
    open: boolean;
    onClose: (reload?: boolean) => void;
    surveyId: string;
    questionId: string;
    type?: string;
}

export const ModalAnswers = ({ open, onClose, questionId, type }: Props) => {
    const [answers, setAnswers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState<any>(null);

    const loadAnswers = async () => {
        setLoading(true);
        try {
            const data = await asyncGetAnswers(questionId);
            setAnswers(data);
        } catch (error) {
            console.error('Error loading answers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadAnswers();
        }
    }, [open, questionId]);

    const formik = useFormik({
        initialValues: {
            label: '',
            description: '',
            status: true,
        },
        validationSchema: Yup.object({
            label: Yup.string().when([], {
                is: () => type === 'linear-label',
                then: (schema) => schema.required('La etiqueta es obligatoria'),
            }),
            description: Yup.string()
                .required('La descripción es obligatoria')
                .test(
                    'unique-description',
                    'Ya existe una respuesta con esta descripción',
                    function (value) {
                        if (!value) return true;
                        // Verificar si hay otra respuesta con la misma descripción
                        const isDuplicate = answers.some(
                            (a: any) =>
                                a.description.toLowerCase().trim() ===
                                    value.toLowerCase().trim() &&
                                a.id !== selectedAnswer?.id
                        );
                        return !isDuplicate;
                    }
                ),
        }),
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (selectedAnswer?.id) {
                    // Para actualizar, no enviamos questionId
                    const payload = {
                        label: values.label,
                        description: values.description,
                        status: values.status,
                    };
                    await asyncUpdateAnswer(selectedAnswer.id, payload);
                    ToastSwal.fire({
                        text: 'Respuesta actualizada exitosamente',
                        icon: 'success',
                        timer: 3000,
                        position: 'center-end',
                    });
                } else {
                    // Para crear, sí enviamos questionId
                    const payload = {
                        questionId: questionId,
                        label: values.label,
                        description: values.description,
                        status: values.status,
                    };
                    await asyncCreateAnswer(payload);
                    ToastSwal.fire({
                        text: 'Respuesta creada exitosamente',
                        icon: 'success',
                        timer: 3000,
                        position: 'center-end',
                    });
                }
                setOpenForm(false);
                setSelectedAnswer(null);
                formik.resetForm();
                await loadAnswers();
            } catch (error) {
                console.error('Error al guardar la respuesta:', error);
                ToastSwal.fire({
                    text: 'Error al guardar la respuesta',
                    icon: 'error',
                    timer: 3000,
                    position: 'center-end',
                });
            } finally {
                setLoading(false);
            }
        },
    });

    const handleAddAnswer = () => {
        setSelectedAnswer(null);
        formik.resetForm();
        setOpenForm(true);
    };

    const handleEditAnswer = (answer: any) => {
        setSelectedAnswer(answer);
        formik.setValues({
            label: answer.label || '',
            description: answer.description || '',
            status: answer.status,
        });
        setOpenForm(true);
    };

    const handleToggleStatus = async (
        answerId: string,
        currentStatus: boolean
    ) => {
        try {
            await asyncUpdateAnswer(answerId, { status: !currentStatus });
            ToastSwal.fire({
                text: `Respuesta ${
                    !currentStatus ? 'activada' : 'desactivada'
                } exitosamente`,
                icon: 'success',
                timer: 3000,
                position: 'center-end',
            });
            await loadAnswers();
        } catch (error) {
            ToastSwal.fire({
                text: 'Error al cambiar el estado',
                icon: 'error',
                timer: 3000,
                position: 'center-end',
            });
        }
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setSelectedAnswer(null);
        formik.resetForm();
    };

    const columns: any[] = [
        ...(type === 'linear-label'
            ? [
                {
                    field: 'label',
                    headerName: 'Etiqueta',
                    minWidth: 150,
                    flex: 1,
                },
            ]
            : []),
        {
            field: 'description',
            headerName: 'Descripción',
            minWidth: 250,
            flex: 2,
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 100,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <Chip
                    label={params.value ? 'ACTIVO' : 'INACTIVO'}
                    size="small"
                    color={params.value ? 'success' : 'error'}
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            minWidth: 100,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => handleEditAnswer(params.row)}
                        >
                            <EditIcon sx={{ color: 'primary.main' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={params.row.status ? 'Desactivar' : 'Activar'}
                    >
                        <IconButton
                            size="small"
                            onClick={() =>
                                handleToggleStatus(
                                    params.row.id,
                                    params.row.status
                                )
                            }
                        >
                            {params.row.status ? (
                                <ToggleOnIcon sx={{ color: 'primary.main' }} />
                            ) : (
                                <ToggleOffIcon sx={{ color: 'primary.main' }} />
                            )}
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            maxWidth={openForm ? 'sm' : 'lg'}
            fullWidth
        >
            <DialogTitle>
                <Typography
                    sx={{
                        fontSize: { lg: 27, xs: 23 },
                        mt: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0 },
                        color: 'primary.main',
                    }}
                >
                    {openForm
                        ? selectedAnswer
                            ? 'Editar Respuesta'
                            : 'Nueva Respuesta'
                        : 'Respuestas'}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {!openForm ? (
                    <Box>
                        <Box sx={{ mb: 2, textAlign: 'right' }}>
                            <LoadingButtonGeneral
                                onClick={handleAddAnswer}
                                title="Agregar Respuesta"
                                startIcon={<AddCircleIcon />}
                            />
                        </Box>
                        <Table
                            columns={columns}
                            rows={answers}
                            loading={loading}
                            autoAdjustHeight
                            disableRowSelectionOnClick
                        />
                    </Box>
                ) : (
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            {type === 'linear-label' && (
                                <Grid item xs={12}>
                                    <FieldText
                                        label="Etiqueta"
                                        name="label"
                                        value={formik.values.label}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        touched={formik.touched.label}
                                        errors={formik.errors.label}
                                        required
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <FieldText
                                    label="Descripción"
                                    name="description"
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    touched={formik.touched.description}
                                    errors={formik.errors.description}
                                    required
                                />
                            </Grid>
                        </Grid>
                    </form>
                )}
            </DialogContent>
            <DialogActions>
                {!openForm ? (
                    <LoadingButtonGeneral
                        onClick={() => onClose(true)}
                        title="Cerrar"
                        variant="outlined"
                    />
                ) : (
                    <>
                        <LoadingButtonGeneral
                            onClick={handleCloseForm}
                            disabled={loading}
                            title="Cancelar"
                            variant="outlined"
                        />
                        <LoadingButtonGeneral
                            onClick={formik.handleSubmit}
                            loading={loading}
                            disabled={loading}
                            title="Guardar"
                        />
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
